/**
 * ChartWidget — the single, reusable recharts wrapper for the Common Dashboard
 * Framework. One component renders every core chart form from a declarative
 * config, so no dashboard hand-rolls recharts again.
 *
 * Supported `type`: line | area | bar | stacked-bar | hbar | pie | donut | scatter.
 * (Specialized forms — risk-matrix, heatmap, gauge, treemap — are separate
 * widget types registered later; they are not recharts-cartesian.)
 *
 * Design (per the dataviz skill):
 * - Categorical colors assigned in FIXED ORDER via chartTheme (never cycled;
 *   a 9th series folds to muted "Other"). Scatter caps at 3 series (all-pairs).
 * - NO dual-axis, ever (one y-scale) — two measures ⇒ two charts.
 * - Legend always present for ≥2 entries (identity never by color alone) and is
 *   click-to-toggle. It is OUR legend, not recharts' — see ChartLegend for why.
 *   Styled tooltip on hover; CSV/table export as the relief for the light
 *   palette's sub-3:1 hues.
 * - Toolbar: fullscreen, download PNG, download CSV. Optional drill-down.
 * - `onPointClick` fires from the MARK on bar/pie/scatter and from the CHART on
 *   line/area (recharts 3 gives a line's own handler a DOM event, not a data
 *   entry — see cartesianClick). Either way the callback receives
 *   `{ payload, dataKey }`; line/area add `entries` (every visible series at
 *   that x) and `activeLabel`.
 * - `showValues` prints each point's value on the chart instead of leaving it to
 *   the tooltip. Opt-in, because it only reads well on a chart with few points —
 *   a twelve-month trend with a label per point is unreadable, and a report the
 *   reader cannot hover (printed, or pasted into a deck) is exactly where the
 *   labels are needed. Zero-valued points are left unlabelled: a "0" floating
 *   above an empty column is noise, and the empty column already says it.
 *   It sets the STARTING position only: the toolbar's # button hands the choice
 *   to the reader, since labels help when reading figures off a slide and get in
 *   the way when comparing shapes.
 * - The legend hides and shows data, and takes the values with it: clicking a
 *   series (cartesian) or a slice (pie/donut) removes it from the chart while
 *   keeping its legend entry struck through, so it can always be brought back.
 */
import React, { useRef, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, LabelList,
} from 'recharts';
import { Maximize2, X, Download, Table as TableIcon, Hash } from 'lucide-react';
import { useChartTheme, seriesColor } from '../utils/chartTheme';
import { CARD, HEADING } from '../ui';

const RADIAL_TYPES = new Set(['pie', 'donut']);

function toCsv(rows, xKey, series) {
  const header = [xKey, ...series.map((s) => s.label || s.key)];
  const lines = [header.join(',')];
  for (const row of rows) {
    const cells = [row[xKey], ...series.map((s) => row[s.key])];
    lines.push(cells.map((c) => (c == null ? '' : `"${String(c).replace(/"/g, '""')}"`)).join(','));
  }
  return lines.join('\n');
}

function download(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function exportPng(containerEl, filename) {
  if (!containerEl) return;
  // Capture the whole recharts wrapper, NOT just the <svg>: recharts renders
  // the legend as an HTML <div> (.recharts-legend-wrapper) that sits OUTSIDE
  // the SVG, so serializing only svg.recharts-surface drops the legend and
  // any HTML overlay — producing an "incomplete" image. html2canvas
  // rasterizes the full DOM subtree (SVG marks + HTML legend) faithfully.
  const target =
    containerEl.querySelector('.recharts-wrapper') ||
    containerEl.querySelector('svg') ||
    containerEl;
  try {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(target, {
      scale: 2,               // crisp, high-DPI export
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
    });
    canvas.toBlob((blob) => blob && download(filename, blob), 'image/png');
  } catch (err) {
    console.error('Chart PNG export failed', err);
  }
}

function ChartTooltip({ active, payload, label, palette, valueFormat }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-md border px-3 py-2 text-xs shadow-md"
      style={{ background: palette.surface, borderColor: palette.axis, color: palette.primaryInk }}
    >
      {label !== undefined && <div className="mb-1 font-semibold">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey ?? p.name} className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span style={{ color: palette.secondaryInk }}>{p.name}:</span>
          <span className="font-medium">{valueFormat ? valueFormat(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function Toolbar({ onFullscreen, onPng, onCsv, isFullscreen, onClose,
                  valuesShown, onToggleValues }) {
  const btn = 'rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700';
  const on = 'rounded p-1 text-blue-600 hover:bg-blue-50';
  return (
    <div className="flex items-center gap-1">
      {onToggleValues && (
        <button type="button" className={valuesShown ? on : btn}
                aria-pressed={valuesShown}
                title={valuesShown ? 'Hide values on the chart'
                                   : 'Show values on the chart'}
                onClick={onToggleValues}>
          <Hash className="h-4 w-4" />
        </button>
      )}
      <button type="button" className={btn} title="Download PNG" onClick={onPng}><Download className="h-4 w-4" /></button>
      <button type="button" className={btn} title="Download CSV" onClick={onCsv}><TableIcon className="h-4 w-4" /></button>
      {isFullscreen ? (
        <button type="button" className={btn} title="Exit fullscreen" onClick={onClose}><X className="h-4 w-4" /></button>
      ) : (
        <button type="button" className={btn} title="Fullscreen" onClick={onFullscreen}><Maximize2 className="h-4 w-4" /></button>
      )}
    </div>
  );
}

/** A value printed on the mark itself, rather than left to the tooltip. */
function valueLabel({ dataKey, palette, valueFormat, position }) {
  return (
    <LabelList
      dataKey={dataKey}
      position={position}
      offset={6}
      style={{ fontSize: 11, fill: palette.secondaryInk, fontWeight: 600 }}
      formatter={(value) => {
        if (value === null || value === undefined || value === 0) return '';
        return valueFormat ? valueFormat(value) : value;
      }}
    />
  );
}

/**
 * The legend, rendered by us rather than by recharts.
 *
 * Recharts 3 builds its legend from an internal store of the series it is
 * currently DRAWING (Legend.js: contextPayload from useLegendPayload, which
 * overwrites any `payload` prop). Hide a series and its entry leaves the legend
 * with it — taking away the only control that turns it back on. No prop fixes
 * that, so the legend lives outside the chart, driven by the same `hidden` set
 * the chart reads.
 *
 * Toggled-off entries stay put, greyed and struck through, and click to restore.
 */
function ChartLegend({ items, hidden, toggle, palette }) {
  if (!items || items.length < 2) return null;
  return (
    <ul className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
      {items.map((item) => {
        const off = hidden.has(item.key);
        return (
          <li key={item.key}>
            <button
              type="button"
              onClick={() => toggle(item.key)}
              aria-pressed={!off}
              title={off ? `Show ${item.label}` : `Hide ${item.label}`}
              className="flex items-center gap-1.5 rounded px-1 py-0.5 text-xs
                         transition-opacity focus:outline-none focus:ring-2
                         focus:ring-blue-400/60"
              style={{ opacity: off ? 0.55 : 1 }}
            >
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: off ? palette.muted : item.color }}
              />
              <span style={{
                color: off ? palette.muted : palette.secondaryInk,
                textDecoration: off ? 'line-through' : 'none',
              }}>
                {item.label}
                {item.valueLabel ? ` — ${item.valueLabel}` : ''}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function renderChart({ type, data, xKey, series, palette, hidden, toggle, valueFormat, onPointClick, showValues }) {
  // Every series is rendered; toggled-off ones are hidden via recharts `hide`.
  // The legend itself is NOT recharts' — see ChartLegend above for why it cannot
  // be, and note there is no <Legend> in any tree below.
  const grid = <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} vertical={false} />;
  const axisProps = { stroke: palette.axis, tick: { fill: palette.muted, fontSize: 12 } };
  const tooltip = <Tooltip content={<ChartTooltip palette={palette} valueFormat={valueFormat} />} />;
  const clickHandler = onPointClick ? (d) => onPointClick(d) : undefined;

  /**
   * Click handling for LINE and AREA lives on the chart, not the mark.
   *
   * recharts 3 spreads handlers onto a Line's curve path via
   * svgPropertiesAndEvents, so `<Line onClick>` receives a bare DOM MouseEvent
   * with no data attached — unlike Bar and Pie, which pass the data entry. A
   * drill-down wired to the mark therefore never fires on a line chart. The
   * chart-level handler gets `activeLabel` + `activePayload` for the hovered x
   * position, which also gives a far bigger hit target than a 3px dot.
   *
   * `entries` carries every series at that x, so a caller with several series
   * can offer all of them rather than guessing which line was meant.
   */
  const cartesianClick = onPointClick
    ? (state) => {
      const entries = state?.activePayload;
      if (!entries || entries.length === 0) return;
      const visible = entries.filter((e) => !hidden.has(e.dataKey));
      const chosen = visible.length ? visible : entries;
      onPointClick({
        payload: chosen[0].payload,
        dataKey: chosen[0].dataKey,
        entries: chosen,
        activeLabel: state.activeLabel,
      });
    }
    : undefined;

  if (RADIAL_TYPES.has(type)) {
    const key = series[0]?.key;
    // Hidden slices are filtered out of the pie; ChartLegend keeps their entry.
    const visible = data.filter((row) => !hidden.has(row[xKey]));
    return (
      <PieChart>
        {tooltip}
        <Pie
          data={visible}
          dataKey={key}
          nameKey={xKey}
          innerRadius={type === 'donut' ? '55%' : 0}
          outerRadius="80%"
          paddingAngle={2}
          onClick={clickHandler}
          isAnimationActive={false}
        >
          {visible.map((entry, i) => (
            <Cell key={i}
                  // Colour follows the row's ORIGINAL position, so hiding one
                  // slice does not recolour the others.
                  fill={seriesColor(palette, data.indexOf(entry))}
                  stroke={palette.surface} strokeWidth={2} />
          ))}
        </Pie>
      </PieChart>
    );
  }

  if (type === 'scatter') {
    // All-pairs form → cap at 3 series (dataviz all-pairs floor).
    const capped = series.slice(0, 3);
    return (
      <ScatterChart>
        {grid}
        <XAxis dataKey={xKey} type="number" {...axisProps} />
        <YAxis {...axisProps} />
        {tooltip}
        {capped.map((s, i) => (
          <Scatter key={s.key} name={s.label || s.key} data={data} dataKey={s.key} fill={seriesColor(palette, i)} hide={hidden.has(s.key)} onClick={clickHandler} />
        ))}
      </ScatterChart>
    );
  }

  if (type === 'area' || type === 'stacked-area') {
    const stacked = type === 'stacked-area';
    return (
      <AreaChart data={data} onClick={cartesianClick}>
        {grid}
        <XAxis dataKey={xKey} {...axisProps} />
        <YAxis {...axisProps} />
        {tooltip}
        {series.map((s, i) => (
          <Area key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
            stackId={stacked ? 'stack' : undefined}
            stroke={seriesColor(palette, i)} fill={seriesColor(palette, i)}
            fillOpacity={0.25} strokeWidth={2} hide={hidden.has(s.key)}
            isAnimationActive={false}>
            {showValues && !hidden.has(s.key)
              && valueLabel({ dataKey: s.key, palette, valueFormat, position: 'top' })}
          </Area>
        ))}
      </AreaChart>
    );
  }

  if (type === 'bar' || type === 'stacked-bar' || type === 'hbar') {
    const stacked = type === 'stacked-bar';
    const horizontal = type === 'hbar';
    return (
      <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'} barCategoryGap="20%">
        {grid}
        {horizontal
          ? (<><XAxis type="number" {...axisProps} /><YAxis type="category" dataKey={xKey} width={120} {...axisProps} /></>)
          : (<><XAxis dataKey={xKey} {...axisProps} /><YAxis {...axisProps} /></>)}
        {tooltip}
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label || s.key}
            stackId={stacked ? 'stack' : undefined}
            fill={seriesColor(palette, i)}
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            hide={hidden.has(s.key)}
            onClick={clickHandler} isAnimationActive={false}>
            {/* Stacked bars label inside the segment; a label above the stack
                would read as the total rather than the segment it belongs to. */}
            {showValues && !hidden.has(s.key)
              && valueLabel({ dataKey: s.key, palette, valueFormat,
                              position: stacked ? 'center'
                                : horizontal ? 'right' : 'top' })}
          </Bar>
        ))}
      </BarChart>
    );
  }

  // default: line
  return (
    <LineChart data={data} onClick={cartesianClick}>
      {grid}
      <XAxis dataKey={xKey} {...axisProps} />
      <YAxis {...axisProps} />
      {tooltip}
      {series.map((s, i) => (
        <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
          stroke={seriesColor(palette, i)} strokeWidth={2} dot={{ r: 3 }}
          activeDot={{ r: 5 }} hide={hidden.has(s.key)}
          isAnimationActive={false}>
          {showValues && !hidden.has(s.key)
            && valueLabel({ dataKey: s.key, palette, valueFormat, position: 'top' })}
        </Line>
      ))}
    </LineChart>
  );
}

/**
 * Legend entries for a chart: one per SERIES for cartesian types, one per SLICE
 * for pie and donut — a pie's identity is its slice names, not its series name.
 */
function legendItems({ type, data, xKey, series, palette, valueFormat, showValues }) {
  if (RADIAL_TYPES.has(type)) {
    const key = series[0]?.key;
    return data.map((row, i) => {
      const value = row?.[key];
      const hasValue = value !== null && value !== undefined;
      return {
        key: row[xKey],
        label: row[xKey],
        color: seriesColor(palette, i),
        // The figure rides in the legend for radial charts: a label outside a
        // ring clips in a small card, and a thin donut band has no room inside.
        valueLabel: showValues && hasValue
          ? (valueFormat ? valueFormat(value) : value) : '',
      };
    });
  }
  return series.map((s, i) => ({
    key: s.key,
    label: s.label || s.key,
    color: seriesColor(palette, i),
    valueLabel: '',
  }));
}

export default function ChartWidget({
  type = 'line',
  data = [],
  xKey = 'name',
  series = [],
  title,
  height = 300,
  valueFormat,
  showValues = false,
  onPointClick,
  loading = false,
  error = null,
  className = '',
}) {
  const { palette } = useChartTheme();
  const [hidden, setHidden] = useState(() => new Set());
  const [fullscreen, setFullscreen] = useState(false);
  // `showValues` sets the starting position; the toolbar toggle then belongs to
  // the reader. Labels help on a slide and get in the way when comparing
  // shapes, and which of those you are doing changes minute to minute.
  const [valuesShown, setValuesShown] = useState(showValues);
  const containerRef = useRef(null);

  const toggle = (key) => {
    if (!key) return;
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const isEmpty = !loading && !error && (!data || data.length === 0);

  // Chart area rendered at a given height: a fixed px height in the card,
  // "100%" in fullscreen (where a flex parent supplies the real height).
  const items = isEmpty ? [] : legendItems({
    type, data, xKey, series, palette, valueFormat, showValues: valuesShown,
  });

  const renderArea = (h) => {
    if (loading) return <div className="h-full animate-pulse rounded bg-gray-100" style={{ height: h }} />;
    if (error) return <div className="flex items-center justify-center text-sm text-red-600" style={{ height: h }}>Failed to load chart</div>;
    if (isEmpty) return <div className="flex items-center justify-center text-sm text-gray-400" style={{ height: h }}>No data for this period</div>;
    return (
      <>
        <ResponsiveContainer width="100%" height={h}>
          {renderChart({ type, data, xKey, series, palette, hidden, toggle, valueFormat, onPointClick, showValues: valuesShown })}
        </ResponsiveContainer>
        <ChartLegend items={items} hidden={hidden} toggle={toggle} palette={palette} />
      </>
    );
  };

  const toolbar = (
    <Toolbar
      isFullscreen={fullscreen}
      onFullscreen={() => setFullscreen(true)}
      onClose={() => setFullscreen(false)}
      valuesShown={valuesShown}
      onToggleValues={() => setValuesShown((v) => !v)}
      onPng={() => exportPng(containerRef.current, `${(title || 'chart').replace(/\s+/g, '_')}.png`)}
      onCsv={() => download(`${(title || 'chart').replace(/\s+/g, '_')}.csv`, new Blob([toCsv(data, xKey, series)], { type: 'text/csv' }))}
    />
  );

  if (fullscreen) {
    // Full-viewport panel: header (auto) + chart area that flexes to fill, so
    // ResponsiveContainer height="100%" resolves against a real height.
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white p-4" ref={containerRef}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800">{title || 'Chart'}</h3>
          {toolbar}
        </div>
        <div className="min-h-0 flex-1">{renderArea('100%')}</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${CARD} p-4 transition-shadow duration-200 hover:shadow-md ${className}`}
    >
      <div className="mb-3 flex items-center justify-between">
        {title ? <h3 className={HEADING}>{title}</h3> : <span />}
        {toolbar}
      </div>
      {renderArea(height)}
    </div>
  );
}
